import { request } from '@umijs/max';

export type Result<T> = {
  code: number;
  message: string;
  success: boolean;
  data: T;
};

export type IPage<T> = {
  records: T[];
  total: number;
  size: number;
  current: number;
  pages: number;
};

export type PauseScopeCode = 'PUSH' | 'ANALYSIS' | 'PLAYBACK' | 'EXCESS_SMS';
export type PauseScope = PauseScopeCode;
export type ResumeType = 'TEMP_24H' | 'TEMP_72H' | 'PERMANENT';

export type ArrearsOverview = {
  suspendTenantCount: number;
  pendingAmount: number;
  autoResumeMonth: number;
  manualWaiveMonth: number;
};

/** 兼容页面原统计字段命名 */
export type ArrearsStats = {
  pausedCount: number;
  pendingAmount: number;
  monthAutoResume: number;
  monthManualWaive: number;
};

export type ArrearsTenantAction = 'RESUME' | 'WAIVE' | 'FORCE_SUSPEND';

export type ArrearsTenant = {
  tenantUserId: number;
  tenantName: string;
  /** 欠费金额 */
  oweAmount: number;
  suspendTime?: string;
  scopes?: PauseScopeCode[];
  /** SUSPENDED / TEMP_RESUMED 等 */
  status: string;
  /** 当前行可执行的操作 */
  actions?: ArrearsTenantAction[];
};

export type ArrearsLogType =
  | 'AUTO_RESUME'
  | 'MANUAL_RESUME'
  | 'WAIVE'
  | 'AUTO_SUSPEND';

export type ArrearsLog = {
  id: number;
  userId: number;
  opsType: ArrearsLogType | string;
  amount?: number;
  reason?: string;
  /** 恢复类操作时可能有 */
  resumeType?: ResumeType | string;
  operator?: string;
  createTime?: string;
  /** 若后端后续补充租户名 */
  tenantName?: string;
};

export type PausePolicy = {
  immediateSuspend: boolean;
  lowBalanceThreshold: number;
  suspendScopes: PauseScopeCode[];
  defaultResumeType: ResumeType;
};

export const RESUME_TYPE_LABEL: Record<ResumeType, string> = {
  TEMP_24H: '限时恢复 24h',
  TEMP_72H: '限时恢复 72h',
  PERMANENT: '永久恢复',
};

export const SCOPE_LABEL: Record<PauseScopeCode, string> = {
  PUSH: '新预警推送',
  ANALYSIS: '算法分析',
  PLAYBACK: '录像回放',
  EXCESS_SMS: '超额短信',
};

export const TENANT_STATUS_META: Record<
  string,
  { label: string; color: string }
> = {
  SUSPENDED: { label: '暂停中', color: 'error' },
  PAUSED: { label: '暂停中', color: 'error' },
  TEMP_RESUMED: { label: '限时恢复', color: 'processing' },
};

export const LOG_TYPE_META: Record<string, { label: string; color: string }> = {
  AUTO_RESUME: { label: '自动恢复', color: 'success' },
  MANUAL_RESUME: { label: '人工恢复', color: 'blue' },
  WAIVE: { label: '豁免', color: 'orange' },
  AUTO_SUSPEND: { label: '自动暂停', color: 'error' },
};

export async function getArrearsOverview() {
  return request<Result<ArrearsOverview>>('/admin/cloud/arrears/overview', {
    method: 'GET',
  });
}

export async function getArrearsStats(): Promise<Result<ArrearsStats>> {
  const res = await getArrearsOverview();
  if (res.code !== 200 || !res.data) {
    return {
      ...res,
      data: {
        pausedCount: 0,
        pendingAmount: 0,
        monthAutoResume: 0,
        monthManualWaive: 0,
      },
    };
  }
  return {
    ...res,
    data: {
      pausedCount: res.data.suspendTenantCount,
      pendingAmount: res.data.pendingAmount,
      monthAutoResume: res.data.autoResumeMonth,
      monthManualWaive: res.data.manualWaiveMonth,
    },
  };
}

export async function getArrearsPausedCount(): Promise<
  Result<{ count: number }>
> {
  const stats = await getArrearsStats();
  return {
    ...stats,
    data: { count: stats.data?.pausedCount ?? 0 },
  };
}

export async function listArrearsTenants(body?: {
  current?: number;
  size?: number;
}) {
  return request<Result<IPage<ArrearsTenant>>>('/admin/cloud/arrears/page', {
    method: 'POST',
    data: {
      current: body?.current ?? 1,
      size: body?.size ?? 10,
    },
  });
}

export async function listArrearsLogs(params?: {
  current?: number;
  size?: number;
}) {
  return request<Result<IPage<ArrearsLog>>>('/admin/cloud/arrears/ops-log', {
    method: 'GET',
    params: {
      current: params?.current ?? 1,
      size: params?.size ?? 10,
    },
  });
}

export async function getPausePolicy() {
  return request<Result<PausePolicy>>('/admin/cloud/suspend-policy', {
    method: 'GET',
  });
}

export async function savePausePolicy(policy: PausePolicy) {
  return request<Result<PausePolicy | null>>('/admin/cloud/suspend-policy', {
    method: 'PUT',
    data: policy,
  });
}

export async function resumeArrears(body: {
  tenantUserId: number;
  resumeType: ResumeType;
  reason: string;
}) {
  return request<Result<null>>('/admin/cloud/arrears/resume', {
    method: 'POST',
    data: body,
  });
}

export async function waiveArrears(body: {
  tenantUserId: number;
  reason: string;
}) {
  return request<Result<null>>('/admin/cloud/arrears/waive', {
    method: 'POST',
    data: body,
  });
}

export async function forcePauseArrears(tenantUserId: number) {
  return request<Result<null>>('/admin/cloud/arrears/force-suspend', {
    method: 'POST',
    data: { tenantUserId },
  });
}
